import { DcBaseComponent } from '@deleteagency/dc';
import { getQueryStringByParams } from 'general/js/url-helper';
import loFindIndex from 'lodash/findIndex';
import loFindLastIndex from 'lodash/findLastIndex';
import loFlow from 'lodash/flow';
import loProperty from 'lodash/property';
import loPartialRight from 'lodash/partialRight';
import loSome from 'lodash/some';
import { AxiosSingular } from '@deleteagency/axios-singular';
import axios from 'axios';
import api from 'general/js/api';
import ArrayHelper from 'general/js/array-helper';
import { propertyStorageService } from 'general/js/property-storage-service/index';

export default class PropertyControlsComponent extends DcBaseComponent {
    constructor(element) {
        super(element);
        this.propertySearchEndpointUrl = this.options.propertySearchEndpointUrl;
        this.endpointUrl = this.options.endpointUrl;
        this.propertyId = this.options.propertyId;
        this.latestSearch = null;
        this.axiosSingular = new AxiosSingular(
            api,
            axios.CancelToken,
            axios.isCancel
        );
        this.nextSearch = null;
        this.currentPropertyIndex = null;
    }

    static getNamespace() {
        return 'property-controls';
    }

    static getRequiredRefs() {
        return ['links'];
    }

    onInit() {
        if (!propertyStorageService.isKeyExists()) return;

        const searches = this.getSearches();
        const searchIndexForCurrentProperty = this.getSearchIndexForCurrentProperty(
            searches
        );

        if (searchIndexForCurrentProperty === -1) return;

        if (searchIndexForCurrentProperty !== searches.length - 1) {
            ArrayHelper.arrayMove(
                searches,
                searchIndexForCurrentProperty,
                searches.length - 1
            );
            propertyStorageService.setStore(searches);
        }

        this.latestSearch = this.getLatestSearch();
        this.currentPropertyIndex = this.getCurrentPropertyIndex(
            this.latestSearch
        );

        if (this.refs.links.backToSearch) {
            this.refs.links.backToSearch.href = this.createBackToSearchUrl();
            this.show(this.refs.links.backToSearch);
        }

        this.addPrevPropertyUrl();

        this.addNextPropertyUrl();
    }

    getSearchIndexForCurrentProperty = (searches) =>
        loFindLastIndex(
            searches,
            loFlow(
                loProperty('propertyCards'),
                loPartialRight(loSome, {
                    propertyID: this.propertyId.toString(),
                })
            )
        );

    getSearches = () => propertyStorageService.getStore().slice(0);

    getLatestSearch = () => {
        const storage = this.getSearches();
        return storage[storage.length - 1];
    };

    createBackToSearchUrl = () => {
        const searchParams = this.getSearchParams();
        const tabHash = searchParams.showMapTab ? 'map' : 'list';

        return `${this.propertySearchEndpointUrl}?${getQueryStringByParams(
            searchParams
        )}#${tabHash}`;
    };

    show = (el) => el.removeAttribute('hidden');

    getSearchParams = (page = 'current') => {
        const { currentPage } = this.latestSearch.pagination;
        const {
            propertySearchSelectedOptions,
        } = this.latestSearch.propertySearchFormModel;

        switch (page) {
            case 'previous':
                page = currentPage - 1;
                break;
            case 'next':
                page = currentPage + 1;
                break;
            default:
                page = currentPage;
        }

        return Object.assign(
            {},
            propertySearchSelectedOptions,
            page >= 1 && { page }
        );
    };

    getCurrentPropertyIndex = ({ propertyCards }) =>
        loFindIndex(
            propertyCards,
            (property) => parseInt(property.propertyID, 10) === this.propertyId
        );

    isFirstWithinLatestSearch = () => this.currentPropertyIndex === 0;

    isLastWithinLatestSearch = ({ propertyCards }) =>
        this.currentPropertyIndex === propertyCards.length - 1;

    getPreviousePropertyUrl = ({ propertyCards }, isExistingSearch = true) => {
        const nextPropertyIndex = this.currentPropertyIndex - 1;

        if (ArrayHelper.isEmpty(propertyCards)) return false;

        return !isExistingSearch
            ? propertyCards[propertyCards.length - 1].propertyUrl
            : propertyCards[nextPropertyIndex].propertyUrl;
    };

    getNextPropertyUrl = ({ propertyCards }, isExistingSearch = true) => {
        let nextPropertyIndex = this.currentPropertyIndex + 1;

        if (!isExistingSearch) {
            nextPropertyIndex = 0;
        }

        if (ArrayHelper.isEmpty(propertyCards)) return false;

        return propertyCards[nextPropertyIndex].propertyUrl;
    };

    async addPrevPropertyUrl() {
        let href = '';

        const { currentPage } = this.latestSearch.pagination;

        if (this.isFirstWithinLatestSearch()) {
            await this.performNextSearch('previous');
            if (currentPage === 1) return;
            href = this.getPreviousePropertyUrl(this.nextSearch, false);
        } else {
            href = this.getPreviousePropertyUrl(this.latestSearch);
        }

        if (href && this.refs.links.prevProperty) {
            this.refs.links.prevProperty.href = href;
            this.show(this.refs.links.prevProperty);
        }
    }

    async addNextPropertyUrl() {
        let href = '';

        if (this.isLastWithinLatestSearch(this.latestSearch)) {
            await this.performNextSearch('next');

            href = this.getNextPropertyUrl(this.nextSearch, false);
        } else {
            href = this.getNextPropertyUrl(this.latestSearch);
        }

        if (href && this.refs.links.nextProperty) {
            this.refs.links.nextProperty.href = href;
            this.show(this.refs.links.nextProperty);
        }
    }

    async performNextSearch(page) {
        const params = this.getSearchParams(page);

        return this.axiosSingular
            .get(this.endpointUrl, {
                params,
            })
            .then(
                (response) => {
                    this.nextSearch = response.data;
                    propertyStorageService.updateStore(this.nextSearch);
                },
                (error) => {
                    if (!axios.isCancel(error)) {
                        // eslint-disable-next-line no-console
                        console.error('Actual error!', error);
                    }
                }
            );
    }
}
